import React, { PureComponent } from 'react';

import { InputAPI } from '@philips-emr/tws-components-api';
import { AutocompleteType, Density, InputType, Placement, TextInputCapitalize } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { bool, element, eventListener, expression, formatters, number, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { validate } from '@philips-emr/tws-core/lib/services/model-service';
import { validators } from '@philips-emr/tws-model';
import { classNames, defaultClasses, enumValues, isDefined, isEnumValid, isFunction, trigger } from '@philips-emr/tws-utils';

import { ReactComponent as HideIcon } from '../../../resources/assets/components/DLS_Hide.svg';
import { ReactComponent as ShowIcon } from '../../../resources/assets/components/DLS_Show.svg';

import InputFormatter from './input-formatter';

import './input.css';

function InputCounter({ length, value = '' }) {
  if (!length && length !== 0) {
    return null;
  }

  const valueLength = value ? String(value).length : 0;

  const counterClasses = {
    'c-input__counter': true,
    'u-color-safety-red': length - valueLength < 0,
  };

  const count = `(${valueLength}/${length})`;

  return <span className={classNames(counterClasses)}>{count}</span>;
}

InputCounter.propTypes = {
  length: number,
  value: string,
};

@AwesomeComponent({ type: ComponentType.DATA_ENTRY, api: InputAPI })
class Input extends PureComponent {
  state = {
    showPassword: false,
  };

  static propTypes = {
    autoCapitalize: oneOf(enumValues(TextInputCapitalize)),
    autoComplete: oneOf(enumValues(AutocompleteType)),
    autoCorrect: bool,
    autoFocus: bool,
    autoSelect: bool,
    className: string,
    density: oneOf(enumValues(Density)),
    enabled: bool,
    enforceLength: bool,
    externalAction: element,
    externalActionPlacement: oneOf([Placement.LEFT, Placement.RIGHT]),
    focused: bool,
    formatters: formatters,
    length: number,
    max: number,
    min: number,
    name: string,
    onBlur: eventListener,
    onChange: eventListener,
    onFocus: eventListener,
    onKeyDown: eventListener,
    onKeyPress: eventListener,
    onKeyUp: eventListener,
    placeholder: expression,
    readOnly: bool,
    showLength: bool,
    showPasswordToggle: bool,
    step: number,
    tabIndex: number,
    type: oneOf(enumValues(InputType)),
    valid: bool,
    value: oneOfType([number, string]),
  };

  static defaultProps = {
    autoCapitalize: TextInputCapitalize.NONE,
    density: Density.REGULAR,
    enabled: true,
    enforceLength: true,
    externalActionPlacement: Placement.RIGHT,
    formatters: {},
    showLength: true,
    showPasswordToggle: true,
    type: InputType.TEXT,
    valid: true,
    value: '',
  };

  getInputProps = () => {
    const propsToIgnore = [
      'autoSelect',
      'children',
      'enforceLength',
      'externalAction',
      'externalActionPlacement',
      'onChange',
      'showLength',
      'showPasswordToggle',
      'size',
      'valid',
    ];

    const propNames = Object.keys(Input.propTypes).filter(
      (propName) => !propsToIgnore.includes(propName)
    );

    return propNames.reduce((props, propName) => {
      if (propName in this.props) {
        props[propName] = this.props[propName];
      }

      return props;
    }, {});
  };

  normalizeProps = (props) => {
    const { showPassword } = this.state;

    const {
      autoComplete,
      autoCorrect,
      enabled,
      placeholder,
      ...otherProps
    } = props;

    const normalizedProps = { ...otherProps };

    if ('autoComplete' in props) {
      // We use it with the capitalized "C" because React requires camelCase for DOM properties, but outputs the correct ones in the end.
      // https://github.com/facebook/react/issues/2882#issuecomment-70931554

      // There seem to be controversies about how to disable autofill/autocomplete on chrome since "off" is being ignored,
      // for now, the most reliable way I found is to provide an invalid key (like "none"), which in turn disables both autofill/autocomplete since there's no history match.
      const isBoolean = autoComplete => typeof autoComplete ===  'boolean';
      const getValueOfOldAutoComplete = autoComplete => autoComplete ? 'on' : 'none';

      if (autoComplete) {
        normalizedProps.autoComplete = isBoolean(autoComplete) ? getValueOfOldAutoComplete(autoComplete) : autoComplete;
      }
    }

    if ('autoCorrect' in props) {
      normalizedProps.autoCorrect = autoCorrect ? 'on' : 'off';
    }

    if ('enabled' in props) {
      normalizedProps.disabled = !enabled;
    }

    if ('placeholder' in props) {
      normalizedProps.placeholder = msg(placeholder);
    }

    if (props.type === InputType.PASSWORD && showPassword) {
      normalizedProps.type = InputType.TEXT;
    }

    return normalizedProps;
  };

  getValueFormatter = () => {
    const { formatters } = this.props;

    return formatters.value;
  };

  getKeyEventObject = (event) => {
    const { value: oldValue } = this.props;
    const {
      altKey,
      cancelable,
      charCode,
      ctrlKey,
      key,
      keyCode,
      preventDefault,
      shiftKey,
      stopImmediatePropagation,
      stopPropagation,
      target,
    } = event;

    const { value } = target;

    return {
      altKey,
      cancelable,
      charCode,
      ctrlKey,
      key,
      keyCode,
      oldValue,
      preventDefault,
      shiftKey,
      stopImmediatePropagation,
      stopPropagation,
      value,
    };
  };

  isLengthValid(value) {
    const { enforceLength, length } = this.props;
    return !length || !enforceLength || String(value).length <= length;
  }

  onChange = ({ value }) => {
    const { max, min, onChange, value: oldValue } = this.props;

    if (!isFunction(onChange) || !this.isLengthValid(value)) {
      return;
    }

    const validationRunner = validate(value);

    const valueFormatter = this.getValueFormatter();

    if (isDefined(valueFormatter)) {
      validationRunner.apply(valueFormatter.name, valueFormatter.options);
    }

    if (isDefined(max)) {
      validationRunner.apply(validators.MAX_VALUE, { max });
    }

    if (isDefined(min)) {
      validationRunner.apply(validators.MIN_VALUE, { min });
    }

    const validations = validationRunner.run();

    onChange({ value, oldValue, validations });
  };

  onKeyDown = (event) => {
    const { onKeyDown } = this.props;
    const params = this.getKeyEventObject(event);

    onKeyDown && onKeyDown(params);
  };

  onKeyPress = (event) => {
    const { onKeyPress } = this.props;
    const params = this.getKeyEventObject(event);

    onKeyPress && onKeyPress(params);
  };

  onKeyUp = (event) => {
    const { onKeyUp } = this.props;
    const params = this.getKeyEventObject(event);

    onKeyUp && onKeyUp(params);
  };

  componentDidMount() {
    const { autoSelect } = this.props;

    if (autoSelect) {
      this.selectInput();
    }
  }

  componentDidUpdate(prevProps) {
    const { focused } = this.props;

    if (prevProps.focused !== focused && focused) {
      this.focusInput();
    }
  }

  focusInput() {
    const { autoSelect } = this.props;

    this.inputRef.current.focus();

    if (autoSelect) {
      this.selectInput();
    }
  }

  selectInput() {
    this.inputRef.current.select();
  }

  renderInput = () => {
    const { onBlur, onFocus, value, ...props } = this.normalizeProps(this.getInputProps());

    return (
      <InputFormatter
        formatter={this.getValueFormatter()}
        onBlur={trigger(onBlur)}
        onChange={this.onChange}
        onFocus={trigger(onFocus)}
        onKeyDown={this.onKeyDown}
        onKeyPress={this.onKeyPress}
        onKeyUp={this.onKeyUp}
        ref={ref => (this.inputRef = ref)}
        value={value}
      >
        <input {...props} className="c-input__input" data-testid="input-element" />
      </InputFormatter>
    );
  };

  onToggleClick = () => {
    this.setState((prevState) => {
      return {
        showPassword: !prevState.showPassword,
      };
    });
  };

  renderPasswordToggle = () => {
    const { showPassword } = this.state;
    const { enabled, readOnly, showPasswordToggle, type } = this.props;

    if (type !== InputType.PASSWORD || !showPasswordToggle || !enabled || readOnly) {
      return null;
    }

    return (
      <div className="c-input__toggle-password u-hide@view u-hide@empty" onClick={this.onToggleClick}>
        {showPassword ? <HideIcon /> : <ShowIcon />}
      </div>
    );
  };

  renderExternalAction = () => {
    const { externalAction, externalActionPlacement } = this.props;

    if (!externalAction) {
      return null;
    }

    const externalActionClassname = classNames({
      'c-input__action': true,
      'u-hide@empty': true,
      'u-hide@view': true,
      [`c-input__action--${externalActionPlacement}`]: isEnumValid(Placement, externalActionPlacement),
    });

    return (
      <div className={externalActionClassname} x-contrast="bg">
        {externalAction}
      </div>
    );
  };

  renderFooter = () => {
    const { enabled, length, readOnly, showLength, value } = this.props;

    if (!length || !enabled || readOnly || !showLength) {
      return null;
    }

    return (
      <div className="c-input__footer">
        <InputCounter length={length} value={value} />
      </div>
    );
  };

  render() {
    const { className, density, externalActionPlacement, valid } = this.props;
    const defClasses = defaultClasses(this.props);

    const clazz = classNames({
      'c-input': true,
      [`c-input--${density}`]: isEnumValid(Density, density),
      'c-input--validated': !valid,
      ...defClasses,
      [className]: className,
    });

    return (
      <div className={clazz} data-testid="input">
        <div className="c-input__box">
          {externalActionPlacement === Placement.LEFT && this.renderExternalAction()}
          {this.renderInput()}
          {this.renderPasswordToggle()}
          {externalActionPlacement === Placement.RIGHT && this.renderExternalAction()}
        </div>
        {this.renderFooter()}
      </div>
    );
  }
}

export default Input;
